/**
 * Step 15 — Sign-off, summary & submission to management.
 */
import { useState } from "react";
import API from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Loader2, AlertCircle, Send, Home } from "lucide-react";
import { toast } from "sonner";

const STEP_LABELS = [
  null, "Arrival & PIN", "Customer Info", "Inspection Consent", "Damage Type",
  "Damage Photos", "Scope of Work", "Moisture Assessment", "Floor Plan",
  "Equipment Placement", "Hazards", "Inventory", "Customer Debrief",
  "Estimate Summary", "Paperwork & Signature", "Sign-off",
];

const STATUS_BADGE = {
  complete:    { cls: "bg-emerald-100 text-emerald-700", label: "Complete" },
  in_progress: { cls: "bg-blue-100 text-blue-700",       label: "In Progress" },
  na:          { cls: "bg-zinc-200 text-zinc-600",        label: "N/A" },
  incomplete:  { cls: "bg-rose-100 text-rose-700",        label: "Incomplete" },
};

export default function Step15SignOff({ workflow, request, requestId }) {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(!!workflow?.submitted_at);

  const steps = workflow?.steps || {};
  const allDone = Array.from({ length: 14 }).every((_, i) => {
    const st = (steps[String(i + 1)] || {}).status;
    return st === "complete" || st === "na";
  });
  const blockers = Array.from({ length: 14 })
    .map((_, i) => ({ n: i + 1, st: (steps[String(i + 1)] || {}).status }))
    .filter(x => x.st !== "complete" && x.st !== "na");

  const submit = async () => {
    setSubmitting(true);
    try {
      await API.post(`/inspection/${requestId}/step/15/submit`);
      setSubmitted(true);
      toast.success("Inspection submitted to management for review");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit");
    } finally { setSubmitting(false); }
  };

  if (submitted) {
    return (
      <div className="space-y-4" data-testid="step-15-submitted">
        <Card className="border-emerald-300 bg-emerald-50">
          <CardContent className="pt-6 pb-6 text-center">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-2" />
            <h2 className="text-lg font-bold text-zinc-900">Inspection Submitted</h2>
            <p className="text-xs text-zinc-700 mt-2 leading-relaxed">
              The job has been sent to management for estimate review with status: <strong>Estimate Pending Review</strong>.
              The manager will be notified, and the customer will receive their full detailed estimate to their ARM account
              once approved.
            </p>
            <p className="text-[10px] text-zinc-500 mt-2">
              A Certificate of Satisfaction will be generated and sent to the customer's ARM account after the job is fully completed.
            </p>
            <Button onClick={() => navigate("/vendor/active")} className="mt-4 h-11 bg-zinc-900 text-white"
                    data-testid="step-15-back-active">
              <Home className="w-4 h-4 mr-1" /> Back to Active Jobs
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="step-15-form">
      <div className="flex items-center gap-2">
        <Send className="w-5 h-5 text-amber-600" />
        <h2 className="text-base font-bold text-zinc-900">Step 15 · Sign-off & Submission</h2>
      </div>

      <Card><CardContent className="pt-3 pb-3">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 mb-2">Status Summary</p>
        <div className="space-y-1">
          {Array.from({ length: 15 }).map((_, i) => {
            const num = i + 1;
            const st = (steps[String(num)] || {}).status || "incomplete";
            const v = STATUS_BADGE[st];
            return (
              <div key={num} className="flex items-center justify-between text-xs" data-testid={`step-15-row-${num}`}>
                <span className="text-zinc-700">{num}. {STEP_LABELS[num]}</span>
                <Badge className={`text-[10px] ${v.cls}`}>{v.label}</Badge>
              </div>
            );
          })}
        </div>
      </CardContent></Card>

      {!allDone && (
        <Card className="border-rose-200 bg-rose-50">
          <CardContent className="pt-4">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-rose-600 mt-0.5" />
              <div>
                <p className="text-xs font-semibold text-zinc-900">Cannot submit yet</p>
                <p className="text-[11px] text-zinc-600 mt-1">
                  Complete or mark Not Applicable on:&nbsp;
                  {blockers.map(b => `Step ${b.n}`).join(", ")}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Button onClick={submit} disabled={!allDone || submitting}
              className="w-full h-14 text-base bg-emerald-600 hover:bg-emerald-700 text-white disabled:bg-zinc-200 disabled:text-zinc-400"
              data-testid="step-15-submit-btn">
        {submitting ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Submitting…</>
          : <><Send className="w-5 h-5 mr-2" /> Submit Inspection to Management</>}
      </Button>
    </div>
  );
}
